"use client";

import React from "react";
import { Compass, PenTool, Layers, ChartLine, Activity } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Feature {
  id: number;
  label: string;
  title: string;
  icon: any;
  description: string;
}
const features: Feature[] = [{
  id: 1,
  label: "LÖSNINGAR",
  title: "E-handel",
  icon: Compass,
  description: "Skalbara AI-drivna plattformar som optimerar kundresan och maximerar konvertering och förbättrad prestanda."
}, {
  id: 2,
  label: "UTBILDNING",
  title: "Utbildning",
  icon: PenTool,
  description: "Intelligenta verktyg för personligt lärande och administrativ effektivisering med fokus på säkerhet och skalbarhet."
}, {
  id: 3,
  label: "LÖSNINGAR",
  title: "Logistik & säkerhet",
  icon: Layers,
  description: "AI-drivna lösningar för att upptäcka risker, förebygga bedrägerier och optimera logistikflöden i realtid."
}, {
  id: 4,
  label: "INNOVATION",
  title: "Digitalisering",
  icon: ChartLine,
  description: "Strategisk AI-implementering som stärker processer, beslut och digital utveckling."
}, {
  id: 5,
  label: "VÅRD",
  title: "Vård & hälsa",
  icon: Activity,
  description: "Digitala AI-plattformar som förbättrar kommunikationen mellan patienter och vårdgivare och effektiviserar vårdprocesser."
}];
function FeatureCard({
  feature,
  index
}: {
  feature: Feature;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const Icon = feature.icon;
  const borderColors = ["border-t-primary", "border-t-ring", "border-t-primary/80", "border-t-ring/80"];
  return <div ref={ref} data-index={index} className={`group cursor-pointer transition-all duration-300 ease-out flex flex-col h-full ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className={`bg-gradient-to-br from-background to-accent border-t-4 ${borderColors[index % 4]} shadow-md p-0 flex flex-col gap-8 rounded-xl group-hover:shadow-xl group-hover:scale-103 transition-all duration-300 ease-out h-full`}>
        <CardHeader className="p-8 md:p-10 pb-4 gap-6 relative">
          <div className="text-xs tracking-widest uppercase mb-2 text-ring font-mono">
            {feature.label}
          </div>
          <Icon strokeWidth={1.5} className="w-14 h-14 text-ring transition-transform duration-300 group-hover:scale-110" />
          <CardTitle className="text-2xl font-default font-bold tracking-tight text-foreground mb-3">
            {feature.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 px-8 md:px-10 pb-10 mt-auto">
          <p className="text-muted-foreground text-sm leading-relaxed font-default">
            {feature.description}
          </p>
        </CardContent>
      </Card>
    </div>;
}
export default function WhatWeDo() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section data-section-id="243191" id="whatwedo" className="bg-background text-foreground overflow-hidden py-20 md:py-28 bg-gradient-to-br from-background via-accent/20 to-background">
      <div className="container mx-auto px-6 md:px-12 max-w-[900px]">
        <div ref={headerRef} className={`mb-24 md:mb-28 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-default font-bold uppercase tracking-widest text-center text-primary mb-6">
            Vad vi gör
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto font-default font-medium text-lg md:text-xl leading-relaxed">Alla egna lösningar levereras som skalbara SaaS-plattformar – utvecklade för långsiktigt partnerskap och tillväxt.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 max-w-[900px] mx-auto">
          {features.map((feature, index) => <FeatureCard key={feature.id} feature={feature} index={index} />)}
        </div>
      </div>
    </section>;
}